/* eslint-disable no-restricted-imports */
import { useEffect, useRef } from "react";
import {
  HotkeyCallback,
  useHotkeys as useHotkeysLib,
  useHotkeysContext,
  Options,
} from "react-hotkeys-hook";

export enum HotkeyScope {
  Overlay = "overlay",
  Page = "page",
}

export type UseHotKeys = {
  key: string;
  callback: HotkeyCallback;
  options?: Options;
};

export /**
 * Represents a keyboard shortcut
 */
type HotkeyShortcut = {
  key: string;
  label: string;
  options?: UseHotKeys["options"];
};

/**
 * Central registry of all keyboard shortcuts used in the application
 */
export const HOTKEYS = {
  global: {
    lockScreen: {
      key: "f12, mod+0",
      label: "Lock screen",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
  },
  page: {
    search: {
      key: "/",
      label: "Search",
      options: { useKey: true },
    },
  },
  overlay: {
    close: {
      key: "esc",
      label: "Close",
    },
    submit: {
      key: "enter",
      label: "Submit",
    },
  },
  scoll: {
    spacebarPressToGrab: {
      key: "space",
      label: "Grab to scroll",
      options: {
        keydown: true,
        keyup: false,
        useKey: true,
      },
    },
    spacebarReleaseGrab: {
      key: "space",
      label: "Release scroll grab",
      options: {
        keydown: false,
        keyup: true,
        useKey: true,
      },
    },
  },
  bundleServices: {
    cancel: {
      key: "backspace",
      label: "Cancel service selection",
    },
    navigate: {
      key: "up, down, shift+up, shift+down",
      label: "Navigate service options",
    },
    selectCurrent: {
      key: "shift+enter",
      label: "Select current service",
    },
    submit: {
      key: "enter",
      label: "Submit service selection",
    },
    resetIndex: {
      key: "tab",
      label: "Reset index",
    },
  },
  scheduler: {
    toggleSidebar: {
      key: "`",
      label: "Toggle sidebar",
      options: { useKey: true },
    },
    viewShortcuts: {
      key: "?",
      label: "View all shortcuts",
      options: { useKey: true },
    },
    close: {
      key: "esc",
      label: "Close",
    },
    search: {
      key: "s",
      label: "Search",
    },
    today: {
      key: "t",
      label: "Today",
    },
    checkInQueue: {
      key: "q",
      label: "Check-in queue",
    },
    waitlist: {
      key: "w",
      label: "Appointment waitlist",
    },
    deletedAppointments: {
      key: "d",
      label: "Deleted appointments",
    },
    bulkUpdateAppointments: {
      key: "b",
      label: "Bulk update appointments",
    },
    dropoffPickup: {
      key: "p",
      label: "Drop-offs & pick-ups",
    },
    moveViewRight: {
      key: "mod+right",
      label: "Move view right",
    },
    moveViewLeft: {
      key: "mod+left",
      label: "Move view left",
    },
    moveViewUp: {
      key: "mod+up",
      label: "Move view up",
    },
    moveViewDown: {
      key: "mod+down",
      label: "Move view down",
    },
    scrollRight: {
      key: "right",
      label: "Scroll view right",
    },
    scrollLeft: {
      key: "left",
      label: "Scroll view left",
    },
    scrollUp: {
      key: "up",
      label: "Scroll view up",
    },
    scrollDown: {
      key: "down",
      label: "Scroll view down",
    },
    createAppointment: {
      key: "a",
      label: "Create appointment",
    },
    createEvent: {
      key: "e",
      label: "Create event",
    },
    filters: {
      key: "f",
      label: "View filters",
    },
    settings: {
      key: "!",
      label: "View settings",
      options: { useKey: true },
    },
    copy: {
      key: "mod+c",
      label: "Copy appointment/event",
    },
    reschedule: {
      key: "mod+x",
      label: "Reschedule appointment/event",
    },
    undo: {
      key: "mod+z",
      label: "Undo appointment update",
    },
    redo: {
      key: "mod+shift+z",
      label: "Redo appointment update",
    },
  },
  medicalRecords: {
    toggleSidebar: {
      key: "`",
      label: "Toggle sidebar",
      options: { useKey: true },
    },
    viewShortcuts: {
      key: "?",
      label: "View all shortcuts",
      options: { useKey: true },
    },
    close: {
      key: "esc",
      label: "Close",
    },
    openHistory: {
      key: "h",
      label: "Open history",
    },
    openTranscription: {
      key: "v",
      label: "Voice dictation",
    },
    navigateDown: {
      key: "mod+down",
      label: "Navigate down section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateUp: {
      key: "mod+up",
      label: "Navigate up section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateRight: {
      key: "mod+right",
      label: "Navigate right section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    navigateLeft: {
      key: "mod+left",
      label: "Navigate left section",
      options: {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
    },
    openInvoice: {
      key: "i",
      label: "Open invoice",
    },
    multiSelect: {
      key: "mod+click",
      label: "Multi-select",
    },
  },
};

/**
 * Private base hook to handle keyboard shortcuts
 */
const useHotkeys = (params: UseHotKeys) => {
  const { key, callback, options } = params;

  const callbackRef = useRef(callback);

  // Update the callback ref when the callback changes.
  // This is to ensure that the callback for the hotkey is always the latest callback while at the
  // same time not needing to use the dependency array for useHotkeys.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useHotkeysLib(
    key,
    (event, hotkeysEvent) => {
      callbackRef.current(event, hotkeysEvent);
    },
    options,
  );
};

type UseBaseHotkeys = {
  shortcut: HotkeyShortcut;
  callback: HotkeyCallback;
  scope: HotkeyScope | undefined;
  isEnabled?: boolean;
};

const useBaseHotkeys = (params: UseBaseHotkeys) => {
  const { shortcut, callback, scope, isEnabled = true } = params;

  useHotkeys({
    key: shortcut.key,
    callback,
    options: {
      scopes: scope ? [scope] : undefined,
      enabled: isEnabled,
      ...shortcut.options,
    },
  });
};

type UsePublicHotkeys = {
  shortcut: HotkeyShortcut;
  callback: HotkeyCallback;
  isEnabled?: boolean;
};

/**
 * Hook for registering keyboard shortcuts at the global level
 *
 * Will be active on all pages and overlay components
 */
export const useGlobalHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: undefined,
  });
};

/**
 * Hook for registering keyboard shortcuts at the page level
 *
 * Will be active on all pages
 */
export const usePageHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: HotkeyScope.Page,
  });
};

/**
 * Hook for registering keyboard shortcuts at the overlay level
 *
 * Will be active on all overlay components
 */
export const useOverlayHotkeys = (params: UsePublicHotkeys) => {
  useBaseHotkeys({
    ...params,
    scope: HotkeyScope.Overlay,
  });
};

export type { HotkeyCallback };

export { useHotkeysContext };
